import React, {useEffect, useState, useRef, useLayoutEffect, useContext, useMemo} from "react";
import { useSelector, useDispatch } from "react-redux";
import { GlobalStore } from "../../reduxStore";
import VehicleResult from "./VehicleResult";
import VehicleSearch from "./VehicleSearch";
import SearchSummaryBar from "./SearchSummaryBar";
import MobileStepper from "@mui/material/MobileStepper";
import { Button } from "@mui/material";
import SearchFilter from "./SearchFilter";
import { translateService, Translate } from "../../services/TranslateService";
import { Card } from "react-bootstrap";
import NoResults from "./NoResults";
import styles from "./VehicleSearch.module.scss";
import CustomTooltip from "../CustomTooltip";
import { IOnSale, IVehicleSearch } from "../../interfaces/VehicleSearch";
import moment from "moment";
import { extractCityFromPlace, extractPostalFromPlace } from "../../services/extractCityFromPlace";
import OnSale from "./OnSale/OnSale";
import { DataContext, ISearchParameters } from "../../contexts/dataContext";
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import DefaultVehicleResult from './DefaultVehicleResult';
import DefaultResults from "./DefaultResultsBeforeSearch.json";
import {isUnderTwentyOne} from "../MakeReservation/util";
import {Steps} from "../reservation-steps/steps";
import {OneWaySearchTab, SearchTab} from "./SearchTab/SearchTab";
import {ActiveFilterIndicators} from "./ActiveFilterIndicators/ActiveFilterIndicators";
import {scrollToElementWithOffset} from "../../GetUrlParams";
import { buildVehicleItem } from "../../services/ga4Ecommerce";
import {createSearchRequests} from "../../services/searchValidation";
import type {ISearchResponse} from "../../interfaces/VehicleSearch";
import {prepareDesktopMap} from "../Map/DeferredMap";
import { assertSearchResponse } from "../../services/searchResponse";
import { applyOneWayUrlFilters, preserveDisplayedOrder, sameList, VehicleType } from "./Filter";
import {markParamsReady, markRequestEnd, markRequestFailed, markRequestStart, markResultsVisible} from '../../services/startupTiming';
import {releaseHostReservation} from '../../services/startupSearch';

// Upper bound on the a2a result set fetched per search. The list shows 10 per page; everything
// beyond that exists only to drop pins on the map, and each vehicle costs ~6 KB on the wire and
// ~11 KB of sessionStorage. A large city was returning 430+ (2.5 MB, 3-6 s), so this trades a
// long tail of distant vehicles for a noticeably lighter desktop search. api3 caps mobile at 100
// on its own (mobile=true), so this bound only applies to desktop.
const MAX_SEARCH_RESULTS = 400;
// NoResults shows a spinner this long before its card ("results may be processing"); the
// results-area hold uses the same grace to decide that an empty list is final (row C2).
const NO_RESULTS_GRACE_MS = 1200;

interface IApiResult {
    count: number,
    vehicles: IVehicleSearch[],
    onsales: IOnSale[],
    extraOnsales?: IOnSale[]
}

export default function SearchResultsContainer({ setIsSearching, setRedirect }: {
  setIsSearching: Function,
  setRedirect: Function,
}) {
  const apiService = useSelector((state: GlobalStore) => state.chauffeurReducer.apiService);
  const dispatch = useDispatch();
  const [searchResults, setSearchResults] = useState<IVehicleSearch[]>([]);
  // One-way results as the API returned them, before the client-side URL filters — those
  // are re-derived below whenever their inputs (notably the ACRISS code set) arrive late.
  const [rawOneWayResults, setRawOneWayResults] = useState<IVehicleSearch[]>([]);
  // The inputs the displayed one-way list was last derived from. The effect below re-derives
  // only when one of them changes — never because searchResults changed, since the sort tab
  // and clearResults write searchResults themselves and must not be undone.
  const oneWayDerivedFor = useRef<{raw: IVehicleSearch[]; types?: VehicleType[]; codeSet: unknown; modelId?: number[]} | null>(null);
  // Mirror of searchResults for the effect below, so it can keep the displayed order without
  // depending on searchResults (which would make it fire on every sort and clear).
  const searchResultsRef = useRef<IVehicleSearch[]>([]);
  searchResultsRef.current = searchResults;
  const [onSaleResults, setOnSaleResults] = useState<IOnSale[]>([]);
  const numPerPage = 10;
  const [page, _setPage] = React.useState(0);
  const [showBusy, setShowBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [totalVehicles, setTotalVehicles] = useState(0);
  const [showNothing, setShowNothing] = useState(false);
  const [detailsVisible, setDetailsVisible] = React.useState(-1);
  const dataContext = useContext(DataContext);
  const {route, mapData, showDefaultResults, changeVirtualPageInfo, oneWayRes, vehicle} = useContext(DataContext);
  const [showFilter, setShowFilter] = useState<boolean>(false); // controls filter view/re-renders only, results separated to show filter when no results on selected filters
  const [showResults, setShowResults] = useState<boolean>(false);
  const [currentPageResults, setCurrentPageResults] = useState<IVehicleSearch[]>( []);
  const [openFilters, setOpenFilters] = useState(false);
  // the full search form collapses into a sticky summary bar while results show (mobile + desktop)
  const [formCollapsed, setFormCollapsed] = useState<boolean>(false);
  const summaryBarActive = formCollapsed && !dataContext.oneWayRes;
  const latestFetch = useRef(0);
  // C2: the results area keeps its last laid-out height while a search is in flight (the
  // cards unmount for the fetch), so the page below the widget does not jump up and back.
  // Measured in doSearch before anything unmounts (and re-measured at every commit that
  // shows content); applied in a layout effect so the collapsed frame is never painted;
  // released when the new content commits.
  const resultsAreaRef = useRef<HTMLDivElement>(null);
  const heldHeight = useRef(0);
  // Both describe the CURRENT search only, reset at the start of every search. searchCompleted is
  // also cleared by clearResults, or the previous search's completion would count the cleared area
  // as settled; searchInFlight is not — a clear does not cancel the fetch, its results still land.
  const searchCompleted = useRef(false);
  const searchInFlight = useRef(false);
  // The customer edited the form and nothing is in flight or requested: the hold is released, or
  // the old list's height would stay as blank space (a screen or more on a phone) between the form
  // and the host page's content until they search. The collapse follows their own input, which
  // CLS excludes. The height is still remembered: the search click puts it back (setSearchParams).
  const editReleased = useRef(false);
  // A search is coming: set by the search click and by any other change of the search parameters
  // (the layout effect below), until doSearch takes over after its 300 ms debounce — so the
  // clearResults that VehicleSearch runs when it re-syncs its dates does not release the hold.
  const searchRequested = useRef(false);
  const requestSearch = useMemo(() => createSearchRequests<ISearchResponse>(), [apiService]);
  const timeoutId = useRef(0);
  const defaultResults = DefaultResults.vehicles;

  useEffect(() => {
    if (route === '/' && !showDefaultResults && !vehicle) {
      if (!mapData?.length) {
        changeVirtualPageInfo(undefined);
      } else {
        changeVirtualPageInfo(!oneWayRes ? 'search' : 'onewaySearch')
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oneWayRes, route, showDefaultResults, mapData]);

  useEffect(() => {
    if (dataContext.onSalesAd !== undefined && dataContext.onSalesAd.length === 0
        && sessionStorage.getItem('retrievingOnSalesAd') !== '1' && apiService?.reservation) {
      sessionStorage.setItem('retrievingOnSalesAd', '1');

      apiService?.reservation.getOnSalesAd().then((response: any) => {
        if (response?.length === 0) {
          // no ads available
          dataContext.changeOnSalesAd(undefined);
        } else {
          dataContext.changeOnSalesAd(response);
        }
        sessionStorage.removeItem('retrievingOnSalesAd');
      }).catch(() => {
        // if error, set to undefined and not try fetch again
        sessionStorage.removeItem('retrievingOnSalesAd');
        dataContext.changeOnSalesAd(undefined);
      })
    }
  }, [dataContext.onSalesAd, apiService?.reservation]);

  useEffect(() => {
    if(dataContext.showDefaultResults && dataContext.mapEnabled && !dataContext.oneWayRes){
      dataContext.changeMapData(defaultResults ?? []); // set map data to default results
    }
  }, [dataContext.showDefaultResults, dataContext.mapEnabled, dataContext.oneWayRes])

  useEffect(() => {
    setDetailsVisible(-1)
  }, [page]);

  // GA4 view_item_list — fire once per distinct page of real search results
  // (results state settles over a few renders, so dedupe on a content signature).
  const lastViewedListSig = useRef<string>("");
  useEffect(() => {
    if (!showResults || !currentPageResults?.length) return;
    const listId = oneWayRes ? "oneway_list" : "a2a_list";
    const listName = oneWayRes ? "OneWay Results" : "A2A Results";
    const sig = `${listId}|${page}|${currentPageResults.map(v => v.id).join(",")}`;
    if (sig === lastViewedListSig.current) return;
    lastViewedListSig.current = sig;
    dataContext.sendGa4Event("view_item_list", {
      item_list_id: listId,
      item_list_name: listName,
      items: currentPageResults.map((v, i) =>
        buildVehicleItem(v, { noVat: dataContext.noVatPrices, index: page * numPerPage + i, listId, listName })
      ),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPageResults, showResults]);

  useEffect(() => {
    if(dataContext.enableOneWay && dataContext.oneWayRoutes.length === 0 && dataContext.oneWayRes){
      // one way must be selected to retrieved routes (to save app startup loading, etc.)
      // only get routes if entire app has oneway feature enabled
      // and when route array is empty (prevents recall every time user returns to search page)
      setShowBusy(true);
      apiService?.vehicle.getOneWayCityRoutes(dataContext.loggedIn ?? false)
          .then(r => {
              // No event on success — see the note above retrieveStarsData.
              dataContext.changeOneWayRoutes(r);
              setErrorMsg("")
              setShowBusy(false);
          })
          .catch(e => {
            setErrorMsg(translateService.translate('vehicleSearch.errorLoadingResults'));
            dataContext.sendGa4Event("resource_fetch_failed", { resource: "oneway_routes" });
            if (dataContext.appContext !== "production") console.log(e);
            setShowBusy(false);
          });
    }
  }, [apiService, dataContext.enableOneWay, dataContext.oneWayRoutes, dataContext.oneWayRes]);

  const checkCouponBeforeSearch = () => {
    // set coupon states if coupon was not set before search (login + user?.discount exists case)
    if (dataContext.searchParameters?.discount && dataContext.searchParameters?.discount !== '' &&
        dataContext.searchParameters?.discount !== dataContext.couponCode) {
      apiService?.vehicle.getCoupon(dataContext.searchParameters.discount)
          .then(r => {
            if (!r.valid) {
              // (this case is for user?.discount set from BE), so code should always be valid
              // if incorrect discount goes to BE then it just ignores it
              dataContext.changeStoredCoupon(null)
              sessionStorage.removeItem("coupon");
              sessionStorage.setItem('urlCodeValid', 'false');
            } else {
              sessionStorage.setItem('urlCodeValid', 'true');
              sessionStorage.setItem('coupon', JSON.stringify({ ...r, code: dataContext.searchParameters?.discount }));
              dataContext.changeStoredCoupon({...r, code: dataContext.searchParameters?.discount});
              dataContext.changeCouponDescription(r.description);
              dataContext.changeCouponCode(dataContext.searchParameters?.discount);
            }}).catch(e => {
        console.log('coupon fail ' + e);
        dataContext.changeStoredCoupon(null);
        sessionStorage.setItem('urlCodeValid', 'false');
        sessionStorage.removeItem("coupon");
      })
    }
  }

  // C2: new search parameters mean the debounced search below is coming, whoever set them (the
  // search click, App's URL handling, a coupon). A layout effect, because VehicleSearch's passive
  // effects — its date re-sync and the clearResults that follows — run before this component's.
  useLayoutEffect(() => {
    if (willSearch(dataContext.searchParameters)) searchRequested.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataContext.searchParameters]);

  useEffect(() => {
    // L13: the parameters the startup search will use are known here; the request
    // itself waits for the debounce below (the report shows that wait as d_debounce).
    if (dataContext.oneWayRes ? (dataContext.searchParameters?.pickupCity && dataContext.searchParameters?.returnCity)
                              : dataContext.searchParameters?.location) {
      markParamsReady(!!apiService);
    }
    //debounce
    clearTimeout(timeoutId.current)
    timeoutId.current = window.setTimeout(() => {
      doSearch().finally(() => {})
    }, 300);

    const UseSearchResults = (thisFetch: number, results: IApiResult) => {
      if (thisFetch === latestFetch.current) {
        searchCompleted.current = true;
        searchInFlight.current = false;
       let onSaleArr: IOnSale[] = [];
        if(results.onsales && results.onsales.length < 10 && results.extraOnsales && results.extraOnsales.length > 0){
          onSaleArr = [...results.onsales];
          let extraOnSaleArr: IOnSale[] = [...results.extraOnsales];

          onSaleArr = onSaleArr.concat(extraOnSaleArr);

        } else {
          onSaleArr = [...results.onsales];
        }
        setSearchResults(results.vehicles ?? []);
        setOnSaleResults(onSaleArr ?? []);
        setTotalVehicles(results.count);
        setErrorMsg("");
        if (dataContext.user)
          sessionStorage.setItem("user", JSON.stringify(dataContext.user));
        sessionStorage.setItem("time", moment().format())
        let scrollAnchorEle = document.getElementById("scrollAnchor");
        if (scrollAnchorEle) {
          scrollToElementWithOffset(scrollAnchorEle, 50); // 50px: to account for some of the header space
        }
      }
    }

    // nothing is coming (e.g. the location was cleared): hold nothing
    const noSearch = () => {
      searchRequested.current = false;
      releaseForEdit();
      setShowBusy(false);
    };

    const doSearch = async () => {
      // C2: measure the results area now if it still has content; the layout effect below
      // holds the last non-empty height while the area is empty or the search is busy.
      // (When the samples were already removed by the location change, the height the
      // effect recorded at their last commit is what is held.)
      const measured = resultsAreaRef.current?.getBoundingClientRect().height ?? 0;
      if (measured > 0) heldHeight.current = measured;
      searchCompleted.current = false;
      searchInFlight.current = false;
      setSearchResults([]);
      // (setSearchParams' willSearch makes the same check; inline here, where it narrows the types)
      if(!dataContext.oneWayRes){
        if(!dataContext.searchParameters?.location || !apiService) {
          noSearch();
          return;
        }
      } else {
        if(!dataContext.searchParameters?.pickupCity || !dataContext.searchParameters?.returnCity || !apiService) {
          noSearch();
          return;
        }
      }
      if (dataContext.route === '/') {changeVirtualPageInfo(undefined)}

      dataContext.changeShowDefaultResults(false);
      dataContext.changeMapData([]);
      setRawOneWayResults([]);
      searchInFlight.current = true;
      searchRequested.current = false;
      editReleased.current = false; // a search not started by the click (URL, coupon) holds here
      setShowBusy(true);
      const thisFetch = ++latestFetch.current;
      markRequestStart(thisFetch);
      if (dataContext.mapEnabled && !dataContext.mobileDevice) prepareDesktopMap();

      // Location + dates + first (original) intent for GA4 funnel analysis. City is always
      // available; postal code only for street/zip searches. first_segment is set once per
      // session (see dataContext) and reflects the original car/van intent, unchanged by later toggles.
      const sp = dataContext.searchParameters;
      const fmtDate = (d: moment.MomentInput) => d ? moment(d).format('YYYY-MM-DD') : undefined;
      const dateMeta = { start_date: fmtDate(sp?.startDate), end_date: fmtDate(sp?.endDate),
                         first_segment: sessionStorage.getItem('first_segment') ?? undefined };
      const a2aMeta = { ...dateMeta, pickup_city: sp?.searchCity ?? extractCityFromPlace(sp?.location), pickup_postal_code: extractPostalFromPlace(sp?.location) };
      const onewayMeta = { ...dateMeta, pickup_city: sp?.pickupCity, dropoff_city: sp?.returnCity };

      if(!dataContext.oneWayRes) {
        checkCouponBeforeSearch(); // if user logs in without opening filters, coupon may not set correctly,
        // this will correctly set all coupon values for services to be used calculated correctly

        try {
          const params = { ...dataContext.searchParameters, showStart: 0, showAmount: MAX_SEARCH_RESULTS };
          // List
          // we take the whole result set in one call because api3 is so slow, show 10 of them in the
          // list view and keep the rest for the map
          const mapData = await requestSearch(JSON.stringify(["a2a", !!dataContext.loggedIn, params]), async () => assertSearchResponse(await apiService.vehicle.searchWithParams(params, !!dataContext.loggedIn)));
          if (thisFetch !== latestFetch.current) return;
          markRequestEnd(thisFetch, mapData.count ?? mapData.vehicles.length, mapData.vehicles.length);
          const start = dataContext.searchParameters.showStart === undefined ? 0 : dataContext.searchParameters.showStart;
          const end = start + (dataContext.searchParameters.showAmount === undefined ? 10 : dataContext.searchParameters.showAmount);
          let results = {
                        count: mapData.count ?? 0,
                        vehicles: mapData.vehicles.slice(start,end),
                        // onsales is genuinely optional — api3 omits both keys when onsales is off
                        onsales: mapData.onsales ?? [],
                        extraOnsales: mapData.extraOnsales ?? []
          };
          dataContext.sendGa4Event("search", {
              search_term: "query",
              search_type: "a2a",
              search_result: "success",
              results_count: results.count,
              ...a2aMeta
          });
          UseSearchResults(thisFetch, results);
          // The full result set — not only the map's: the list and its filters are derived from
          // mapData (VehicleSearch / Filter), so with the map off (?map=false) skipping this left
          // the list empty and showed "no results". The map itself only renders when enabled.
          dataContext.changeMapData(mapData.vehicles ?? []);
          setShowBusy(false);
        } catch (e) {
          if (thisFetch !== latestFetch.current) return;
          markRequestFailed(thisFetch);
          searchCompleted.current = true;
          searchInFlight.current = false;
          setErrorMsg(translateService.translate('vehicleSearch.errorLoadingResults'));
          if (dataContext.appContext !== "production") console.log(e);
          dataContext.sendGa4Event("search", {
              search_term: "query",
              search_type: "a2a",
              search_result: "failure",
              results_count: 0,
              ...a2aMeta
          });
          setShowBusy(false);
          let scrollAnchorEle = document.getElementById("scrollAnchor");
          if (scrollAnchorEle) {scrollToElementWithOffset(scrollAnchorEle, 50)}
        }
      } else {
        try {
          const params = { ...dataContext.searchParameters, showStart: 0, showAmount: 1000 };
          // List
          const sharedMapData = await requestSearch(JSON.stringify(["oneway", !!dataContext.loggedIn, params]), async () => assertSearchResponse(await apiService.vehicle.oneWaySearchWithParams(params, dataContext.loggedIn ?? false)));
          if (thisFetch !== latestFetch.current) return;
          // requestSearch hands the same response to every caller of an in-flight duplicate,
          // so filter a shallow copy rather than the shared object.
          const mapData = { ...sharedMapData };
          // /oneway-search has no vehicle-type parameter at all (unlike a2a's carModel), so a
          // URL-seeded acriss filter (?acriss=...) is applied client-side here instead, reusing
          // the same matching used for the a2a checkboxes — silently, without an interactive
          // filter control in the one-way results tab. Once applied it stays applied for the rest
          // of the one-way session (nothing here clears acrissCodeFromUrl the way the a2a
          // SearchFilter effect does, since that component never mounts in one-way mode).
          const rawVehicles: IVehicleSearch[] = mapData.vehicles ?? [];
          setRawOneWayResults(rawVehicles);
          const filtered = applyOneWayUrlFilters(rawVehicles, dataContext.acrissCodeFromUrl, dataContext.acrissCodeSet, dataContext.modelId);
          oneWayDerivedFor.current = {raw: rawVehicles, types: dataContext.acrissCodeFromUrl, codeSet: dataContext.acrissCodeSet, modelId: dataContext.modelId};
          if (filtered !== rawVehicles) { // a filter actually applied; otherwise keep the API's own count
            mapData.vehicles = filtered;
            mapData.count = filtered.length;
          }
          // after the URL filter, so an emptied result set reports 'empty' like an empty response
          markRequestEnd(thisFetch, mapData.count ?? mapData.vehicles.length, mapData.vehicles.length);
          let results: IApiResult = {
            count: mapData.count ?? 0,
            vehicles: mapData.vehicles, //.slice(start,end),
            // do not restrict one-way count, this will be used for sorting and will get updated after sorting and...
            // changing page or searchResults values will select the required data for displaying per page view (under 10)
            onsales: mapData.onsales ?? [],
            extraOnsales: mapData.extraOnsales ?? []
          };
          dataContext.sendGa4Event("search", {
              search_term: "query",
              search_type: "oneway",
              search_result: "success",
              results_count: results.count,
              ...onewayMeta
          });
          UseSearchResults(thisFetch, results);
          // Always, as in a2a above: mapData is the full result set, not only the map's.
          dataContext.changeMapData(mapData.vehicles ?? []);
          setShowBusy(false);
        } catch (e) {
          if (thisFetch !== latestFetch.current) return;
          markRequestFailed(thisFetch);
          searchCompleted.current = true;
          searchInFlight.current = false;
          setErrorMsg(translateService.translate('vehicleSearch.errorLoadingResults'));
          if (dataContext.appContext !== "production") console.log(e);
          dataContext.sendGa4Event("search", {
              search_term: "query",
              search_type: "oneway",
              search_result: "failure",
              results_count: 0,
              ...onewayMeta
          });
          setShowBusy(false);
          let scrollAnchorEle = document.getElementById("scrollAnchor");
          if (scrollAnchorEle) {scrollToElementWithOffset(scrollAnchorEle, 50)}
        }
      }
      setShowNothing(false);
      setIsSearching(true)
    }
    return () => {clearTimeout(timeoutId.current); latestFetch.current++;};
    // oneWayRes too: doSearch reads it from this render. A ?oneway link with a known city sets the
    // location at mount, before one-way switches on (enableOneWay arrives in an effect), and the
    // debounced search would otherwise run as a2a. The re-run drops that timer and any late result.
  }, [dataContext.searchParameters, dataContext.oneWayRes]);

  // @review-regression 2026-09-28 PR#147 P2: the ACRISS code set loads independently of the search. When
  // it lands after the one-way results, the callback above filtered with `undefined` — pass-through by
  // design — and nothing re-ran, so `?acriss=` silently never applied. Derive from the raw results.
  useEffect(() => {
    if (!dataContext.oneWayRes || !rawOneWayResults.length) return;
    const types = dataContext.acrissCodeFromUrl;
    const d = oneWayDerivedFor.current;
    // Inputs compared by content: context may hand over an equal array with a new identity, and
    // that must not count as a change (it would throw away the customer's sort for nothing).
    // The code set only matters with body types to filter by: without them a code set that loads
    // or reloads changes nothing, and re-deriving would send the customer back to page 1.
    if (d && d.raw === rawOneWayResults && sameList(d.types, types)
        && (!types?.length || d.codeSet === dataContext.acrissCodeSet)
        && sameList(d.modelId, dataContext.modelId)) return;
    const filtered = preserveDisplayedOrder(
        applyOneWayUrlFilters(rawOneWayResults, types, dataContext.acrissCodeSet, dataContext.modelId),
        searchResultsRef.current);
    oneWayDerivedFor.current = {raw: rawOneWayResults, types, codeSet: dataContext.acrissCodeSet, modelId: dataContext.modelId};
    setSearchResults(filtered);
    setTotalVehicles(filtered.length);
    // Always, as in the search callback: the sort tab reads mapData even with the map off.
    dataContext.changeMapData(filtered);
  }, [rawOneWayResults, dataContext.oneWayRes, dataContext.acrissCodeFromUrl, dataContext.acrissCodeSet, dataContext.modelId]);

  useEffect(() => {
    const reservationCreated = sessionStorage.getItem("reservationCreated") ?? undefined;
    const paymentPending = sessionStorage.getItem('paymentPending') ?? undefined;
    if (reservationCreated && !paymentPending) {
      // Reservation flag is hydrated but no payment is in flight — the user has
      // navigated back to search after a stale session, so wipe selections so
      // they don't re-enter a half-finished flow.
      if (reservationCreated === 'true') {
        dataContext.clearSelections();
        dispatch({ type: "CLEAR_SELECTIONS", payload: null });
      }
    }

    // #todo: do we need this coupon retrieval here still?
    let c = sessionStorage.getItem("coupon");
    if (c) {
      dataContext.changeStoredCoupon(JSON.parse(c!));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function setSearchParams(v: any) {
    let params: ISearchParameters;
    if (typeof v === 'function')
      params = v(dataContext.searchParameters);
    else
      params = v;
    if (params) {
      // C2: a search click puts the remembered height back now, in the click's own task, so the
      // growth falls inside the 500 ms after input that CLS excludes. Leaving it to doSearch would
      // not: it runs after a 300 ms debounce, plus a render on a slow phone.
      if (params !== dataContext.searchParameters && willSearch(params)) {
        searchRequested.current = true;
        editReleased.current = false;
        const area = resultsAreaRef.current;
        if (area && heldHeight.current > area.getBoundingClientRect().height) area.style.minHeight = `${heldHeight.current}px`;
      }
      dataContext.changeSearchParameters(params);
    }
  }

  // the same check doSearch makes before it fetches
  function willSearch(params?: ISearchParameters) {
    return !!apiService && (dataContext.oneWayRes ? !!(params?.pickupCity && params?.returnCity) : !!params?.location);
  }

  // C2: see editReleased
  function releaseForEdit() {
    editReleased.current = true;
    resultsAreaRef.current?.style.removeProperty('min-height');
  }

  function selectVehicle(vehicle: IVehicleSearch) {
    if (vehicle.id !== dataContext.vehicle?.id) {
      sessionStorage.removeItem("customer");
      sessionStorage.removeItem("services");
    }

    if(dataContext.customer && dataContext.vehicle){
      if( dataContext.customer.dateOfBirth !== '' ||  dataContext.customer.personalIdCode !== ''){
        if(isUnderTwentyOne(dataContext.customer.dateOfBirth,  dataContext.customer.personalIdCode) &&  vehicle){
          if(vehicle.vehicleData.noundertwentyone){
            dataContext.changeTwentyModal(true);
            return;
          }
        }
      }
    }

    dataContext.changeVehicle(vehicle);
    dataContext.changeMapData([vehicle])
    dataContext.changeSearchParameters(dataContext.searchParameters);

    if (dataContext.user)
      sessionStorage.setItem("user", JSON.stringify(dataContext.user));
      sessionStorage.setItem("page", "1")
      dataContext.changeRoute("/makereservation")
      dataContext.changePage(1)
  }

  const clearResults = () => {
    if (!searchInFlight.current && !searchRequested.current) releaseForEdit();
    searchCompleted.current = false;
    setSearchResults([]);
    setRawOneWayResults([]);
    oneWayDerivedFor.current = null;
    dataContext.changeMapData([]);
    setTotalVehicles(0);
    setShowNothing(true);
    setShowFilter(false);
    setShowResults(false);
  };

  const setPage = (p: number) => {
    _setPage(() => {
      const newPage = p;
      if (!dataContext.oneWayRes) {
        if(dataContext.filteredVehicles){
          const startIndex = newPage * numPerPage;
          const endIndex = Math.min(startIndex + numPerPage, dataContext.filteredVehicles?.length);
          setCurrentPageResults(dataContext.filteredVehicles.slice(startIndex, endIndex));
        } else {
          setCurrentPageResults([]);
        }
      } else {
        if(searchResults) {
          const startIndex = page * numPerPage;
          const endIndex = Math.min(startIndex + numPerPage, totalVehicles);
          setCurrentPageResults(searchResults.slice(startIndex, endIndex));
        } else {
          setCurrentPageResults([]);
        }
      }
      return newPage;
    });
  };

  useEffect(() => {
    if (!dataContext.oneWayRes) {
      if(dataContext.filteredVehicles){
        const startIndex = page * numPerPage;
        const endIndex = Math.min(startIndex + numPerPage, dataContext.filteredVehicles?.length);
        setCurrentPageResults(dataContext.filteredVehicles.slice(startIndex, endIndex));
      } else {
        setCurrentPageResults([]);
      }
    } else {
      if(searchResults.length) {
        const startIndex = page * numPerPage;
        const endIndex = Math.min(startIndex + numPerPage, totalVehicles);
        setCurrentPageResults(searchResults.slice(startIndex, endIndex));
      } else {
        setCurrentPageResults([]);
      }
    }
  }, [dataContext.filteredVehicles, page, numPerPage, searchResults]);

  // Fetching testimonials.json (the review-star ratings) is a static asset load, not a
  // user search — but it used to fire GA4 `search`, on every results render, whether or
  // not anyone searched. Together with the one-way route lookup above that was 110,439 of
  // 245,010 `search` events in a 30-day window: 45% of the funnel's entry step was noise,
  // which made the search->select drop-off unreadable. `search` now means a search.
  //
  // Only FAILURES are reported now, as `resource_fetch_failed`. The successful case was
  // ~110k events a month recording that a file loaded, which nobody reads; the failures
  // are the part worth alerting on. Note the event name says "failed" deliberately — an
  // event called `resource_fetch` that only fired on failure would invite someone to
  // compute a failure rate from it and get 100%.
  //
  // Trade-off: without the success event there is no exact denominator, so absolute
  // failure counts replace a failure rate. Both fetches run at most once per session
  // (sessionStorage-cached), so failures/sessions is a fair approximation if a rate is
  // ever needed.
  //
  // `search_term: "query"` was dropped too: that literal was filling GA4's built-in
  // Search term dimension with the word "query".
  const retrieveStarsData = async () => {
    sessionStorage.setItem('starsUpdatedDS', '1'); // 1 indicates that retrieval in progress
    try{
      const response = await fetch(dataContext.rootUrl+'/testimonials.json');
      const data = await response.json();
      // 'starsUpdatedDS': stars data update date time value (only fetch if last update was > 24 hours ago)
      // using sessionStorage as it updates faster than react states (used to prevent fetch before last call gets return value)
      sessionStorage.setItem('starsUpdatedDS', moment().format('DD.MM.YYYY HH:mm').toString())
      dataContext.changeStars(data);
    } catch (e) {
      if (dataContext.appContext !== "production") console.log(e);
      // remove item indicator so fetch can be tried again on next search:
      sessionStorage.removeItem('starsUpdatedDS');
      dataContext.sendGa4Event("resource_fetch_failed", { resource: "testimonials" });
    }
  }

  const getStarValue = () => {
    const starsLastRetrievedData = sessionStorage.getItem('starsUpdatedDS') ?? undefined;
    if (starsLastRetrievedData === '1') return; // retrieval in progress
    if(!starsLastRetrievedData || starsLastRetrievedData === '0') {
      retrieveStarsData();
    } else if (starsLastRetrievedData && starsLastRetrievedData !== '0') {
      if (moment(starsLastRetrievedData, 'DD.MM.YYYY HH:mm').isValid() &&
          moment(starsLastRetrievedData, 'DD.MM.YYYY HH:mm') < moment().subtract(24, 'hours'))
      {
        retrieveStarsData();
      }
    }
  }

  const retrieveAcrissData = async () => {
    sessionStorage.setItem('acrissCodesUpdatedDS', '1'); // 1 indicates that retrieval in progress
    try{
      const response = await fetch(dataContext.v3ApiUrl + '/anonymous/vehicles/get-acriss');
      const data = await response.json();
      // 'acrissCodesUpdatedDS': stars data update date time value (only fetch if last update was > 24 hours ago)
      // using sessionStorage as it updates faster than react states (used to prevent fetch before last call gets return value)
      sessionStorage.setItem('acrissCodesUpdatedDS', moment().format('DD.MM.YYYY HH:mm').toString())
      dataContext.changeAcrissCodeSet(data.data);
    } catch (e) {
      if (dataContext.appContext !== "production") console.log(e);
      // remove item indicator so fetch can be tried again on next search:
      sessionStorage.removeItem('acrissCodesUpdatedDS');
    }
  }

  const getAcrissValues = () => {
    const acrissLastRetrievedData = sessionStorage.getItem('acrissCodesUpdatedDS') ?? undefined;
    if (acrissLastRetrievedData === '1') return; // retrieval in progress
    if(!acrissLastRetrievedData || acrissLastRetrievedData === '0') {
      retrieveAcrissData();
    } else if (acrissLastRetrievedData && acrissLastRetrievedData !== '0') {
      if (moment(acrissLastRetrievedData, 'DD.MM.YYYY HH:mm').isValid() &&
          moment(acrissLastRetrievedData, 'DD.MM.YYYY HH:mm') < moment().subtract(24, 'hours'))
      {
        retrieveAcrissData();
      }
    }
  }

  useEffect(() => {
    if(searchResults.length !==0 && !showBusy ){
      setShowFilter(true);
      setShowResults(true);
    } else if (searchResults.length===0 && openFilters && !showBusy && !showNothing) {
      // no results with filtered selections
      setShowFilter(true);
      setShowResults(false);
    } else {
      setShowFilter(false);
      setShowResults(false);
    }
    getStarValue();
    getAcrissValues();
  }, [showBusy, searchResults]);

  // L13: the first page of results is committed and will paint on the next frame. The
  // ref is what the frame callback re-reads: cleared, replaced or unmounted content
  // resets it (the cleanup runs on every change), so nothing is reported as painted
  // that was not. A layout effect, not a passive one: passive effects can flush after
  // the next animation frame, so the ref must follow the commit before any frame runs.
  const resultsShownRef = useRef(false);
  useLayoutEffect(() => {
    const shown = showResults && !showBusy && currentPageResults.length > 0;
    resultsShownRef.current = shown;
    if (shown) {
      const fetchId = latestFetch.current;
      markResultsVisible(fetchId, () => resultsShownRef.current && latestFetch.current === fetchId);
    }
    return () => { resultsShownRef.current = false; };
  }, [showResults, showBusy, currentPageResults]);
  useLayoutEffect(() => {
    const area = resultsAreaRef.current;
    if (!area) return;
    const content = Array.from(area.children).reduce((h, child) => h + child.getBoundingClientRect().height, 0);
    // Release only on SETTLED content. Everything in between is held: the samples leave on the
    // location change (clearResults runs on every form change — showNothing is not a terminal
    // state), the response arrives one commit before the cards (showResults and the page
    // slice are set by effects), and in that gap NoResults shows its one-second spinner while
    // filteredVehicles is still being derived. A frame can paint in any of those gaps.
    // One-way shows nothing in the area until a one-way search runs (the samples are a2a-only and
    // the route list loads with showBusy of its own), so with no search started there is nothing
    // to hold for: holding would leave the old height as blank space under the one-way form.
    const oneWayIdle = dataContext.oneWayRes && !searchInFlight.current && !searchCompleted.current;
    // An edit with nothing in flight holds nothing either (editReleased) — but unlike one-way, the
    // height is kept for the search click to put back.
    const settled = oneWayIdle || editReleased.current || (!showBusy && (
        (dataContext.showDefaultResults && content > 0)          // the sample results
        // the cards — the flags alone are not enough: showResults flips before filteredVehicles is
        // derived, and currentPageResults lags filteredVehicles by a commit, while NoResults shows
        || (showResults && currentPageResults.length > 0 && content > 0
            && (dataContext.oneWayRes ? searchResults.length > 0 : (dataContext.filteredVehicles?.length ?? 0) > 0))
        // a failed search with nothing in the area (the error card is outside it). When NoResults
        // shows instead, the error is held through its grace like an empty result: releasing to its
        // spinner dropped the page below into view and pushed it back a second later.
        || (!!errorMsg && content === 0)));
    // A genuine empty result (count 0) is NOT settled here: NoResults shows its spinner first, and
    // releasing to the spinner would shift twice (to the spinner, then to the card a second later).
    // It is held through the same grace as an all-filtered-out result and released to the card.
    // data-pending (while not settled, or settled with an empty area) hides the App's version line
    // through CSS: it sits right under this area and was thrown off-screen when content arrived — a
    // large layout shift (index.scss).
    if (!settled) {
      area.dataset.pending = '';
      // hold only when there is more to hold than what is on screen (after a grace release
      // the remembered height IS the NoResults card, and re-holding it would be a no-op write)
      if (heldHeight.current > content) area.style.minHeight = `${heldHeight.current}px`;
      // NoResults on screen after a completed search is either its transitional spinner
      // (filteredVehicles still being derived) or a real "all filtered out" — the state
      // cannot tell them apart. NoResults reveals its card after one second; if it is still
      // there after that, nothing is coming and the hold must go, or the page keeps blank
      // space below an empty result.
      if (!showBusy && content > 0 && searchCompleted.current) {
        const release = window.setTimeout(() => {
          area.style.minHeight = '';
          delete area.dataset.pending;
          // what is on screen now (the NoResults card) is the height worth remembering —
          // not the old list, or the next effect run would reapply it for another grace
          heldHeight.current = area.getBoundingClientRect().height;
          releaseHostReservation();
        }, NO_RESULTS_GRACE_MS);
        return () => window.clearTimeout(release);
      }
      return;
    }
    area.style.minHeight = '';
    // Settled but empty: hide the version line only while nothing has been shown yet — a landing
    // whose search found no place, where App can still restore the samples without any input.
    // After a form edit (results were shown, heldHeight > 0) or on an idle one-way form, content
    // only comes after the customer's search tap, inside the input window CLS excludes — so the
    // line can show. The error card, outside the area, is final either way.
    // (heldHeight is read before the update below.)
    if (content === 0 && !errorMsg && !oneWayIdle && heldHeight.current === 0) area.dataset.pending = '';
    else delete area.dataset.pending;
    // Remember the area's own height (its hold just cleared), not the children's sum: the
    // margins between cards are part of what the page below sits under (~96 px on a phone page).
    // Never remember an empty area — except an idle one-way form, where the old list's height
    // must be forgotten so the first one-way search grows from the form, not from blank space.
    if (content > 0) heldHeight.current = area.getBoundingClientRect().height;
    else if (oneWayIdle) heldHeight.current = 0;
    // A landing that skipped the samples kept the host page's 3,320 px reservation; the
    // first settled result (or error) is the moment it can go.
    if (searchCompleted.current) releaseHostReservation();
  }, [showBusy, currentPageResults, showResults, dataContext.showDefaultResults, dataContext.filteredVehicles, errorMsg, showNothing, totalVehicles, dataContext.oneWayRes]);

  // Collapse the search form into the sticky summary bar whenever a search runs or results are
  // visible (mobile + desktop). Expanding back is manual (the bar's Muokkaa / edit button).
  useEffect(() => {
    if (!dataContext.oneWayRes && (showBusy || showResults)) {
      setFormCollapsed(true);
    }
  }, [showBusy, showResults, dataContext.oneWayRes]);

  const expandSearchForm = () => {
    setFormCollapsed(false);
    // let the form render, then bring the location field into view
    setTimeout(() => document.getElementById('google-search-location')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
  };

  // The map view's summary bar (rendered in App) asks for the form via this event — the map
  // closes and the search form expands in the list view.
  useEffect(() => {
    const handler = () => expandSearchForm();
    window.addEventListener('rrm-expand-search', handler);
    return () => window.removeEventListener('rrm-expand-search', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hide App's floating map button while the sticky summary bar is visible so it never overlaps —
  // the sticky bar owns map open/close instead (rule in index.scss).
  // When the full search form is expanded (summaryBarActive false), the floating button reappears.
  const hideFloatingMapBtn = !!dataContext.mobileDevice && summaryBarActive;
  useEffect(() => {
    if (hideFloatingMapBtn) { document.body.setAttribute('data-rrm-summary-bar', '1'); }
    else { document.body.removeAttribute('data-rrm-summary-bar'); }
    return () => { document.body.removeAttribute('data-rrm-summary-bar'); };
  }, [hideFloatingMapBtn]);

  useEffect(() => {
    if(page !== 0) { // only do this once
      setPage(0);
    }
  }, [dataContext.filteredParams, searchResults, dataContext.sortingTab]);

  const PageStepper = () => {
    const totalPages = !dataContext.oneWayRes ? Math.ceil((dataContext.filteredVehicles?.length || 0) / numPerPage) :
        Math.ceil((totalVehicles || 0) / numPerPage);

    return (
        <MobileStepper
            steps={totalPages}
            variant="text"
            style={{
              flexGrow: 1,
              borderRadius: '8px',
              boxShadow: '0 3px 6px rgb(0 0 0 / 12%)',
              marginBottom: '1.5rem'
            }}
            activeStep={page}
            position="static"
            backButton={
              <span>
          <CustomTooltip className="tooltip-link"
                 zIndex={2}
                 content={<Translate content={'vehicleSearch.firstPage'} />}
          >
            <Button size="small"
                    className={styles.prevNextButton}
                    variant="text"
                    onClick={() => {
                      setPage(0);
                      let scrollAnchorEle = document.getElementById("scrollAnchor");
                      if (scrollAnchorEle) {scrollToElementWithOffset(scrollAnchorEle, 50)}
                      dataContext.sendGa4Event("paginate_search_results", {
                        direction: "home",
                        page_number: 1,
                        search_type: "a2a"
                      });
                    }}
                    disabled={page <= 0}>
              &lt;&lt;
            </Button>
          </CustomTooltip>
          <Button size="small"
                  className={styles.prevNextButton}
                  onClick={() => {
                    setPage(page - 1);
                    let scrollAnchorEle = document.getElementById("scrollAnchor");
                    if (scrollAnchorEle) {scrollToElementWithOffset(scrollAnchorEle, 50)}
                    dataContext.sendGa4Event("paginate_search_results", {
                      direction: "previous",
                      page_number: page-1,
                      search_type: "a2a"
                    });
                  }}
                  disabled={page <= 0}>
            &lt;
          </Button>
        </span>
            }
            nextButton={
              <span>
          <Button size="small"
                  className={styles.prevNextButton}
                  onClick={() => {
                    setPage(page + 1);
                    let scrollAnchorEle = document.getElementById("scrollAnchor");
                    if (scrollAnchorEle) {scrollToElementWithOffset(scrollAnchorEle, 50)}
                    dataContext.sendGa4Event("paginate_search_results", {
                      direction: "next",
                      page_number: page+1,
                      search_type: "a2a"
                    });
                  }}
                  disabled={page + 1 >= totalPages}>
            &gt;
          </Button>
          <CustomTooltip className="tooltip-link"
                 zIndex={2}
                 content={<Translate content={'vehicleSearch.lastPage'} />}
          >
            <Button size="small"
                    className={styles.prevNextButton}
                    onClick={() => {
                      setPage(totalPages - 1);
                      let scrollAnchorEle = document.getElementById("scrollAnchor");
                      if (scrollAnchorEle) {scrollToElementWithOffset(scrollAnchorEle, 50)}
                      dataContext.sendGa4Event("paginate_search_results", {
                        direction: "end",
                        page_number: totalPages - 1,
                        search_type: "a2a"
                      });
                    }}
                    disabled={page + 1 >= totalPages}>
              &gt;&gt;
            </Button>
          </CustomTooltip>
        </span>
            }
        />
    )
  }

  return <div className={styles.searchContainer}
              style={{maxWidth: (oneWayRes && !(sessionStorage.getItem('oneWaySearchInit') ?? undefined)) ? 'max-content' : '768px'}}>
    {summaryBarActive &&
        <SearchSummaryBar
            location={dataContext.searchParameters?.location}
            pickupDate={dataContext.searchParameters?.pickupDate}
            returnDate={dataContext.searchParameters?.returnDate}
            onEdit={expandSearchForm}
            setOpenFilters={setOpenFilters}
            loading={showBusy}
            // Opens the fullscreen map (App owns the state; the floating .mapBtn is hidden while
            // the bar is active so it never overlaps the map). Mobile only — desktop shows the
            // side map already and keeps its own map controls.
            onMapToggle={() => window.dispatchEvent(new Event('rrm-open-map'))}
            showMapButton={!!(dataContext.mobileDevice && dataContext.mapEnabled && dataContext.searchParameters?.location && !dataContext.vehicle)}
        />}
    <div style={summaryBarActive ? { display: 'none' } : undefined}>
        <VehicleSearch isSearching={showBusy} setErrorMsg={setErrorMsg} setSearchParams={setSearchParams} clearResults={clearResults} />
    </div>
    {!!errorMsg &&
        <Card className={`card-body mb-4`}>
          <div className="text-center pt-3">{errorMsg}</div>
        </Card>}
    <span id='scrollAnchor'></span>
    <Backdrop
        sx={{ color: '#fbb040', zIndex: (theme) => theme.zIndex.drawer + 10005 }}
        open={showBusy} >
      <CircularProgress color="inherit" />
      <div style={{marginLeft: '5px'}}><Translate content={'vehicleSearch1.loading'} /></div>
    </Backdrop>
    <div ref={resultsAreaRef} className="r24-results-area">
    { !dataContext.oneWayRes && dataContext.showDefaultResults ? <div>
      <div style={{fontWeight: 600, textAlign:"center", justifyContent: "center", padding: 10, fontSize: 18, fontStyle: "italic" }}>
        {translateService.translate('vehicleSearch1.belowSamplesFillLocation')}
      </div>
      { defaultResults && defaultResults.map((vehicle, i) => {
        return <DefaultVehicleResult key={i} vehicle={vehicle}
                                     priorityImage={i === 0}
                                     noVat={dataContext.noVatPrices}
                                     setRedirect={setRedirect}
                                     selectVehicle={()=> {
                                       document.getElementById("scrollAnchor")?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                       (document.querySelector('.searchbox-input') as HTMLElement)?.focus();
                                     }}
                                     detailsVisible={i === detailsVisible}
                                     setDetailsVisible={() => {setDetailsVisible(() => { return i === detailsVisible ? -1 : i });
                                        dataContext.sendGa4Event("view_item", { items: [buildVehicleItem(vehicle, { noVat: dataContext.noVatPrices })] });}}
        /> })
      }
      <PageStepper/>
    </div> :
        <>
          {!dataContext.oneWayRes ?
              showResults && dataContext.filteredVehicles && dataContext.filteredVehicles.length > 0 && <Steps /> :
              showResults && searchResults.length > 0 && <Steps/> }
          {showFilter && !dataContext.oneWayRes ? <SearchFilter openFilters={openFilters} setCloseFilters={setOpenFilters} /> : null }
        {showResults && !dataContext.oneWayRes && dataContext.filteredVehicles && dataContext.filteredVehicles.length > 0 ?
            <OnSale sales={onSaleResults} selectVehicle={selectVehicle} onSalesAd={dataContext.onSalesAd}/> : null}
        {/* On mobile the sticky summary bar already carries the location, dates, segment and
            filter opener, so the active-filter indicators row is redundant there — show it only
            when the bar isn't active (desktop, or while the form is expanded). */}
        {!summaryBarActive && !dataContext.oneWayRes && showResults && dataContext.filteredVehicles && dataContext.filteredVehicles.length > 0 ?
            <ActiveFilterIndicators setOpenFilters={setOpenFilters} /> : null}
        {!dataContext.oneWayRes && showResults && dataContext.filteredVehicles && dataContext.filteredVehicles.length > 0 ?
            <SearchTab /> : null}
          {dataContext.oneWayRes && showResults && searchResults ?
              <OneWaySearchTab oneWayVehicleList={dataContext.mapData} setSearchResults={setSearchResults} noVatPrices={dataContext.noVatPrices}/> : null }
        {!dataContext.oneWayRes ?
            dataContext.filteredVehicles && dataContext.filteredVehicles.length > 0 && showResults ? (
                <>
                  {!showBusy && currentPageResults.map((vehicle, i) => {
                    return(
                      <VehicleResult key={vehicle.id} vehicle={vehicle}
                                     stars={dataContext.stars ? dataContext.stars[vehicle.id] : undefined}
                                     setRedirect={setRedirect} selectVehicle={selectVehicle}
                                     detailsVisible={i === detailsVisible}
                                     setDetailsVisible={() => {setDetailsVisible(() => { return i === detailsVisible ? -1 : i });
                                       dataContext.sendGa4Event("view_item", { items: [buildVehicleItem(vehicle, { noVat: dataContext.noVatPrices })] });}}
                                     setBusy={setShowBusy}
                                     noVat={dataContext.noVatPrices}/>
                    )
                  })
                  }
                  <PageStepper/>
                </>
            ) : <> {(!showBusy && !showNothing && dataContext.filteredVehicles?.length === 0) ? <NoResults /> : null} </>
            :
            <div> {searchResults && showResults ? (
                    <>
                      {!showBusy && currentPageResults.map((vehicle, i) => {
                        return <VehicleResult key={vehicle.id} vehicle={vehicle}
                                              stars={dataContext.stars ? dataContext.stars[vehicle.id] : undefined}
                                              setRedirect={setRedirect} selectVehicle={selectVehicle}
                                              detailsVisible={i === detailsVisible}
                                              setDetailsVisible={() => {setDetailsVisible(() => { return i === detailsVisible ? -1 : i });
                                                dataContext.sendGa4Event("view_item", { items: [buildVehicleItem(vehicle, { noVat: dataContext.noVatPrices })] });}}
                                              setBusy={setShowBusy}
                                              noVat={dataContext.noVatPrices}/>
                      })
                      }
                      <PageStepper/>
                    </>
                )
                : <> {(!showBusy && !showNothing) && <NoResults /> } </> }
            </div>
        }
      </>
    }
    </div>
  </div>
}
